import React from 'react';
import { useTranslation } from 'react-i18next';

import { Field, Button, Badge, EmptyState, Select, Skeleton, type SelectOption } from '@/nova/components';
import {
  assignCommission,
  createCommissionPlan,
  detachCommission,
  fetchCommissionStructure,
  getAffiliateError,
} from '../../api';
import type { CommissionPlan, CommissionPlanType, CommissionPlanInput, CommissionTier } from '../../types';
import { useAffiliateResource } from '../../hooks/useAffiliateResource';
import { Notice } from '../Notice/Notice';
import { GroupLabel, SegChips, SegChip, FieldGroup, Helper } from '../formControls.styles';
import {
  Section,
  SectionLabel,
  CurrentList,
  CurrentRow,
  PlanMeta,
  PlanName,
  PlanDesc,
  Form,
  TierList,
  TierRow,
  TierFields,
  BusyAction,
} from './CommissionSection.styles';

export type CommissionSectionProps = {
  casinoInstanceId: string;
  affiliateId: string;
  /** Plans already in use across the roster: offered as "Same deal as another affiliate" (the vendor has no plan list,
   *  and creating a fresh plan for every affiliate piles up duplicates). */
  reusablePlans?: CommissionPlan[];
  onToast: (message: string) => void;
  /** A plan was assigned or removed on the platform. The page re-reads its roster here, not only when the sheet
   *  closes: a sheet closed while the request was in flight re-read the roster before the change landed, and the card
   *  kept the old plan. Called even if the section is gone by then. */
  onChanged?: () => void;
};

const TYPE_OPTIONS: { value: CommissionPlanType; label: string }[] = [
  { value: 'revshare', label: 'Rev Share' },
  { value: 'cpa', label: 'CPA' },
  { value: 'hybrid', label: 'Hybrid' },
  { value: 'fixed', label: 'Fixed fee' },
];

type TierRowState = { min: string; pct: string };

const toNum = (value: string): number => {
  const n = parseFloat(value);
  return Number.isFinite(n) ? n : NaN;
};
const isPositive = (value: string): boolean => {
  const n = toNum(value);
  return Number.isFinite(n) && n > 0;
};
/** A share of revenue: above 0, at most 100. */
const isPercent = (value: string): boolean => isPositive(value) && toNum(value) <= 100;

/** Give the affiliate a plan already in use, or create a new one. */
type PlanMode = 'existing' | 'new';

/**
 * Commission plan for one affiliate, inside the drill-down. Reads the current assignment(s) and lets
 * the owner create a plan (rev-share / CPA / hybrid / fixed) and assign it as the affiliate's
 * "all brands" default. Assigning replaces the plan the affiliate is on (the click is the confirmation); a
 * plan managed in the admin console (`foreign_assignment`) is surfaced read-only, not overwritten. An
 * assign or a remove holds its button busy until the server has answered AND the section has re-read
 * the plan, so what it shows next is the server's state, not the state from before the click.
 *
 * First pass deliberately omits brand-scoped assignments, `from_date` scoping and editing a plan's
 * tiers after creation (managed in the console). See the affiliate integration plan.
 */
export const CommissionSection = ({
  casinoInstanceId,
  affiliateId,
  reusablePlans,
  onToast,
  onChanged,
}: CommissionSectionProps): React.ReactElement => {
  const { t } = useTranslation();
  const fetcher = React.useCallback((ci: string) => fetchCommissionStructure(ci, affiliateId), [affiliateId]);
  const structure = useAffiliateResource(casinoInstanceId, fetcher);

  const typeLabels: Record<string, string> = {
    'Rev Share': t('Rev Share'),
    CPA: t('CPA'),
    Hybrid: t('Hybrid'),
    'Fixed fee': t('Fixed fee'),
  };

  const [type, setType] = React.useState<CommissionPlanType>('revshare');
  const [name, setName] = React.useState('');
  // Until the owner types a name, the plan is named after its terms ("Rev share 25%").
  const [nameTouched, setNameTouched] = React.useState(false);
  const [percent, setPercent] = React.useState('');
  const [amount, setAmount] = React.useState('');
  const [fixedAmount, setFixedAmount] = React.useState('');
  const [fixedMode, setFixedMode] = React.useState<'full_month' | 'prorated'>('full_month');
  const [tiered, setTiered] = React.useState(false);
  const [tiers, setTiers] = React.useState<TierRowState[]>([{ min: '0', pct: '' }]);
  // Which action is on its way: its own button spins, every control stays locked until it settles.
  const [busy, setBusy] = React.useState<'assign' | 'remove' | null>(null);
  const saving = busy !== null;
  // A `foreign_assignment` result: the current plan is console-managed and cannot be replaced here.
  const [foreignMessage, setForeignMessage] = React.useState<string | null>(null);

  const describePlan = React.useCallback(
    (plan: CommissionPlan): string => {
      const pct = plan.config?.percent ?? 0;
      const amt = plan.config?.amount ?? 0;
      const fee = plan.config?.fixed_fee_amount ?? 0;
      switch (plan.type) {
        case 'revshare':
          return plan.tiered ? t('Tiered rev share') : t('Rev share {{percent}}%', { percent: pct });
        case 'cpa':
          return t('CPA {{amount}}', { amount: amt });
        case 'hybrid':
          return t('Rev share {{percent}}% + CPA {{amount}}', { percent: pct, amount: amt });
        case 'fixed':
          return t('Fixed fee {{amount}}', { amount: fee });
        default:
          return plan.name;
      }
    },
    [t],
  );

  const configValid = React.useMemo((): boolean => {
    switch (type) {
      case 'revshare':
        return tiered
          ? tiers.some((row) => isPositive(row.pct)) && tiers.every((row) => !row.pct.trim() || isPercent(row.pct))
          : isPercent(percent);
      case 'cpa':
        return isPositive(amount);
      case 'hybrid':
        return isPercent(percent) && isPositive(amount);
      case 'fixed':
        return isPositive(fixedAmount);
      default:
        return false;
    }
  }, [type, tiered, tiers, percent, amount, fixedAmount]);

  // The terms as a plan, to name it after them while the owner has not typed a name.
  const suggestedName = React.useMemo((): string => {
    if (!configValid) return '';
    const config =
      type === 'fixed' ? { fixed_fee_amount: toNum(fixedAmount) } : { percent: toNum(percent), amount: toNum(amount) };
    return describePlan({ id: '', name: '', type, tiered: type === 'revshare' && tiered, config });
  }, [configValid, type, tiered, percent, amount, fixedAmount, describePlan]);
  const planName = nameTouched ? name : suggestedName;

  const canAssign = planName.trim().length > 0 && configValid && !saving;

  const buildInput = React.useCallback((): CommissionPlanInput => {
    const base: CommissionPlanInput = { name: planName.trim(), type };
    switch (type) {
      case 'revshare':
        if (tiered) {
          const rows: CommissionTier[] = tiers
            .filter((row) => isPositive(row.pct))
            .map((row) => ({
              min_revenue: Number.isFinite(toNum(row.min)) ? toNum(row.min) : 0,
              percent: toNum(row.pct),
            }));
          return { ...base, tiered: true, tiers: rows, config: {} };
        }
        return { ...base, config: { percent: toNum(percent) } };
      case 'cpa':
        return { ...base, config: { amount: toNum(amount) } };
      case 'hybrid':
        return { ...base, config: { percent: toNum(percent), amount: toNum(amount) } };
      case 'fixed':
        return { ...base, config: { fixed_fee_amount: toNum(fixedAmount), fixed_fee_mode: fixedMode } };
      default:
        return base;
    }
  }, [planName, type, tiered, tiers, percent, amount, fixedAmount, fixedMode]);
  const resetForm = React.useCallback(() => {
    setName('');
    setNameTouched(false);
    setPercent('');
    setAmount('');
    setFixedAmount('');
    setTiered(false);
    setTiers([{ min: '0', pct: '' }]);
    setForeignMessage(null);
  }, []);

  // Assign a plan as the "all brands" default, replacing the current one: pressing Assign IS the confirmation, so
  // `confirm_replace` always goes (without it the vendor answers 409 `would_replace` and asks again). Resolves once the
  // section has re-read the plan, so the caller's busy state covers the whole round trip.
  const doAssign = React.useCallback(
    async (planId: string): Promise<void> => {
      try {
        await assignCommission(casinoInstanceId, affiliateId, {
          plan_id: planId,
          brand_id: null,
          data_scope: 'all',
          confirm_replace: true,
        });
      } catch (err: unknown) {
        const { message, code } = getAffiliateError(err);
        // A console-managed plan can't be replaced through the partner API, confirm or not.
        if (code === 'foreign_assignment') setForeignMessage(message);
        else onToast(message);
        return;
      }
      onChanged?.();
      await structure.reload();
      onToast(t('Commission plan assigned'));
      resetForm();
    },
    [casinoInstanceId, affiliateId, onToast, onChanged, t, resetForm, structure],
  );

  const onAssign = React.useCallback(async () => {
    if (!canAssign) return;
    setBusy('assign');
    setForeignMessage(null);
    try {
      const plan = await createCommissionPlan(casinoInstanceId, buildInput());
      await doAssign(plan.id);
    } catch (err: unknown) {
      onToast(getAffiliateError(err).message);
    } finally {
      setBusy(null);
    }
  }, [canAssign, casinoInstanceId, buildInput, doAssign, onToast]);

  // Plans already in use elsewhere in the roster that this affiliate does not have yet.
  const currentPlanIds = React.useMemo(
    () => new Set((structure.data?.assignments ?? []).map((a) => a.plan?.id).filter(Boolean)),
    [structure.data],
  );
  const offeredPlans = React.useMemo(
    () => (reusablePlans ?? []).filter((plan) => !currentPlanIds.has(plan.id)),
    [reusablePlans, currentPlanIds],
  );
  const [mode, setMode] = React.useState<PlanMode>('existing');
  const [existingPlanId, setExistingPlanId] = React.useState('');
  const effectiveMode: PlanMode = offeredPlans.length > 0 ? mode : 'new';
  // The picked plan, else the first offered (a stored pick that is no longer offered falls back).
  const pickedPlan = offeredPlans.find((plan) => plan.id === existingPlanId) ?? offeredPlans[0];
  const planOptions: SelectOption[] = offeredPlans.map((plan) => ({
    value: plan.id,
    label: `${plan.name} · ${describePlan(plan)}`,
  }));
  // The pick and the mode are locked while an assignment is on its way.
  const pickPlan = (planId: string): void => {
    if (saving) return;
    setExistingPlanId(planId);
  };
  const pickMode = (next: PlanMode): void => {
    if (saving || next === effectiveMode) return;
    setMode(next);
  };
  const onAssignExisting = React.useCallback(async () => {
    if (!pickedPlan || saving) return;
    setBusy('assign');
    setForeignMessage(null);
    try {
      await doAssign(pickedPlan.id);
    } finally {
      setBusy(null);
    }
  }, [pickedPlan, saving, doAssign]);

  const onDetach = React.useCallback(async () => {
    if (saving) return;
    setBusy('remove');
    try {
      const result = await detachCommission(casinoInstanceId, affiliateId);
      if (result.detached) onChanged?.();
      await structure.reload();
      onToast(result.detached ? t('Commission plan removed') : t('No commission plan to remove'));
    } catch (err: unknown) {
      onToast(getAffiliateError(err).message);
    } finally {
      setBusy(null);
    }
  }, [saving, casinoInstanceId, affiliateId, onToast, onChanged, t, structure]);

  const addTier = React.useCallback(() => setTiers((rows) => [...rows, { min: '', pct: '' }]), []);
  const removeTier = React.useCallback((index: number) => setTiers((rows) => rows.filter((_, i) => i !== index)), []);
  const updateTier = React.useCallback((index: number, key: keyof TierRowState, value: string) => {
    setTiers((rows) => rows.map((row, i) => (i === index ? { ...row, [key]: value } : row)));
  }, []);

  const assignments = structure.data?.assignments ?? [];

  return (
    <Section>
      <SectionLabel text="Commission plan" tag="span" />

      {structure.loading ? (
        <Skeleton height={40} block />
      ) : structure.error ? (
        // A failed read must not read as "no plan": that would tempt the owner to assign a duplicate.
        <Notice
          icon="warning"
          title={t('Could not load the commission plan')}
          body={structure.error}
          actionLabel={t('Retry')}
          onAction={structure.reload}
        />
      ) : assignments.length === 0 ? (
        <EmptyState text={t('No commission plan yet')} />
      ) : (
        <CurrentList>
          {assignments.map((assignment) => {
            const isDefault = !assignment.brand_id;
            return (
              <CurrentRow key={assignment.assignment_id}>
                <PlanMeta>
                  <PlanName text={assignment.plan.name} tag="span" />
                  <PlanDesc
                    text={`${describePlan(assignment.plan)} · ${isDefault ? t('All brands') : assignment.brand_name ?? t('One brand')}`}
                    tag="span"
                  />
                </PlanMeta>
                {isDefault ? (
                  <BusyAction $busy={busy === 'remove'} aria-busy={busy === 'remove'}>
                    <Button
                      label={t('Remove')}
                      variant="outline"
                      icon={busy === 'remove' ? 'refresh' : 'trash'}
                      onClick={onDetach}
                      disabled={saving}
                    />
                  </BusyAction>
                ) : (
                  <Badge label={t('Console')} tone="neutral" />
                )}
              </CurrentRow>
            );
          })}
        </CurrentList>
      )}

      {foreignMessage ? (
        <Notice icon="warning" title={t('Managed in the console')} body={foreignMessage} />
      ) : null}

      <Form>
        <GroupLabel text="Set a commission plan" tag="span" />
        {offeredPlans.length > 0 ? (
          <SegChips>
            <SegChip
              type="button"
              $selected={effectiveMode === 'existing'}
              aria-pressed={effectiveMode === 'existing'}
              disabled={saving}
              onClick={() => pickMode('existing')}
            >
              {t('Same deal as another affiliate')}
            </SegChip>
            <SegChip
              type="button"
              $selected={effectiveMode === 'new'}
              aria-pressed={effectiveMode === 'new'}
              disabled={saving}
              onClick={() => pickMode('new')}
            >
              {t('New plan')}
            </SegChip>
          </SegChips>
        ) : null}

        {effectiveMode === 'existing' && pickedPlan ? (
          <>
            <Select
              label={t('Plan')}
              value={pickedPlan.id}
              onChange={pickPlan}
              options={planOptions}
              disabled={saving}
            />
            <Helper text="The plan is assigned as this affiliate's default across all brands." tag="span" />
            <BusyAction $busy={busy === 'assign'} aria-busy={busy === 'assign'}>
              <Button
                label={t('Assign plan')}
                variant="primary"
                icon={busy === 'assign' ? 'refresh' : 'check'}
                onClick={onAssignExisting}
                disabled={saving}
              />
            </BusyAction>
          </>
        ) : (
          <>
            <SegChips>
              {TYPE_OPTIONS.map((option) => (
                <SegChip
                  key={option.value}
                  type="button"
                  $selected={type === option.value}
                  aria-pressed={type === option.value}
                  onClick={() => setType(option.value)}
                >
                  {typeLabels[option.label] ?? option.label}
                </SegChip>
              ))}
            </SegChips>

            <Field
              label={t('Plan name')}
              value={planName}
              placeholder={t('Partner deal')}
              onChange={(value) => {
                setName(value);
                setNameTouched(true);
              }}
            />

            {type === 'revshare' ? (
              <FieldGroup>
                <SegChips>
                  <SegChip type="button" $selected={!tiered} aria-pressed={!tiered} onClick={() => setTiered(false)}>
                    {t('Flat rate')}
                  </SegChip>
                  <SegChip type="button" $selected={tiered} aria-pressed={tiered} onClick={() => setTiered(true)}>
                    {t('Tiered')}
                  </SegChip>
                </SegChips>
                {tiered ? (
                  <TierList>
                    {/* Index keys are acceptable here: the inputs are controlled (value comes from
                    state, so a removal never mis-binds a value) and rows are only appended or
                    removed, never reordered. */}
                    {tiers.map((row, index) => (
                      <TierRow key={index}>
                        <TierFields>
                          <Field
                            label={t('Min revenue')}
                            value={row.min}
                            placeholder="0"
                            numeric
                            onChange={(value) => updateTier(index, 'min', value)}
                          />
                          <Field
                            label={t('Rate %')}
                            value={row.pct}
                            placeholder="20"
                            numeric
                            suffix="%"
                            onChange={(value) => updateTier(index, 'pct', value)}
                          />
                        </TierFields>
                        {tiers.length > 1 ? (
                          <Button
                            label={t('Remove')}
                            variant="outline"
                            icon="trash"
                            onClick={() => removeTier(index)}
                          />
                        ) : null}
                      </TierRow>
                    ))}
                    <Button label={t('Add tier')} variant="outline" icon="plus" onClick={addTier} />
                  </TierList>
                ) : (
                  <Field
                    label={t('Rev share %')}
                    value={percent}
                    placeholder="25"
                    numeric
                    suffix="%"
                    onChange={setPercent}
                  />
                )}
              </FieldGroup>
            ) : null}

            {type === 'cpa' ? (
              <Field label={t('CPA amount')} value={amount} placeholder="100" numeric onChange={setAmount} />
            ) : null}

            {type === 'hybrid' ? (
              <FieldGroup>
                <Field
                  label={t('Rev share %')}
                  value={percent}
                  placeholder="25"
                  numeric
                  suffix="%"
                  onChange={setPercent}
                />
                <Field label={t('CPA amount')} value={amount} placeholder="100" numeric onChange={setAmount} />
              </FieldGroup>
            ) : null}

            {type === 'fixed' ? (
              <FieldGroup>
                <Field
                  label={t('Fixed fee amount')}
                  value={fixedAmount}
                  placeholder="500"
                  numeric
                  onChange={setFixedAmount}
                />
                <SegChips>
                  <SegChip
                    type="button"
                    $selected={fixedMode === 'full_month'}
                    aria-pressed={fixedMode === 'full_month'}
                    onClick={() => setFixedMode('full_month')}
                  >
                    {t('Full month')}
                  </SegChip>
                  <SegChip
                    type="button"
                    $selected={fixedMode === 'prorated'}
                    aria-pressed={fixedMode === 'prorated'}
                    onClick={() => setFixedMode('prorated')}
                  >
                    {t('Prorated')}
                  </SegChip>
                </SegChips>
              </FieldGroup>
            ) : null}

            <Helper text="The plan is created and assigned as this affiliate's default across all brands." tag="span" />
            <BusyAction $busy={busy === 'assign'} aria-busy={busy === 'assign'}>
              <Button
                label={t('Assign plan')}
                variant="primary"
                icon={busy === 'assign' ? 'refresh' : 'check'}
                onClick={onAssign}
                disabled={!canAssign}
              />
            </BusyAction>
          </>
        )}
      </Form>
    </Section>
  );
};

export default CommissionSection;
